import { z } from 'zod';
import { createZodFormResolver, useFormControls, Validation } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    GeneralArea,
    GeneralAuthenticatorType,
    GeneralSignInMethod,
    GeneralUserAccessResult,
    GeneralUserVerificationRequirementDto,
    useRepositoryMutation,
} from '@mews-commander/data-access';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import {
    checkForConsent,
    checkForForced2FA,
    checkForHardDeviceApproval,
    getDeviceFingerprintPayload,
    INITIAL_SIGN_IN_PARAMS,
    initializeUserSession,
} from '../../../../ducks/actions';
import { NEW_SIGN_IN_FORM } from '../../../constants';
import { FormStep, SignInEventHandlers } from '../useSignInRouter';

export enum FormField {
    Password = 'password',
    NameOrEmail = 'nameOrEmail',
}

export function useSignInCredentialsForm({
    step,
    onEmailSubmit,
    onTwoFactorCheck,
    getCaptchaToken,
}: Omit<SignInEventHandlers, 'onPasswordReset'> & {
    step: Exclude<FormStep, '2fa'>;
    getCaptchaToken: () => string;
}) {
    const signInEmailFormProps = useEmailSubmitHandler({ onEmailSubmit });
    const signInCredentialsFormProps = useCredentialsSubmitHandler({ onTwoFactorCheck, getCaptchaToken });

    const formProps = {
        email: signInEmailFormProps,
        credentials: signInCredentialsFormProps,
    };

    return useFormControls<SignInFormValues>({
        id: `${NEW_SIGN_IN_FORM}-signIn`,
        defaultValues: { [FormField.NameOrEmail]: '', [FormField.Password]: '' },
        ...formProps[step],
    });
}

const emailFieldSchema = z.string().min(1, TextKey.ValidateEmailOrUsername);

const emailSchema = z.object({
    [FormField.NameOrEmail]: emailFieldSchema,
    [FormField.Password]: z.string(),
});

const credentialsSchema = z.object({
    [FormField.NameOrEmail]: emailFieldSchema,
    [FormField.Password]: Validation.String.Required,
});

export type SignInFormValues = z.infer<typeof emailSchema>;

const signInEmailResolver = createZodFormResolver(emailSchema);
const signInCredentialsResolver = createZodFormResolver(credentialsSchema);

function useEmailSubmitHandler({ onEmailSubmit }: Pick<SignInEventHandlers, 'onEmailSubmit'>) {
    const { mutateAsync: getSignInInfo } = useRepositoryMutation('generalUsers', 'getSignInInfo');

    const onSubmit = async ({ nameOrEmail }: SignInFormValues) => {
        try {
            const signInData = await getSignInInfo({
                userIdentifier: nameOrEmail,
                device: getDeviceFingerprintPayload(),
                application: GeneralArea.Commander,
                userVerification: GeneralUserVerificationRequirementDto.Preferred,
            });

            onEmailSubmit({ signInData, nameOrEmail });
            // eslint-disable-next-line @mews/no-empty-catch
        } catch (error) {
            // Mute errors from promise rejections in submit calls.
            // Mutation errors are handled in the mutation itself.
        }
    };

    return { resolver: signInEmailResolver, onSubmit };
}

interface CredentialsSubmitHandlerOptions {
    onTwoFactorCheck: SignInEventHandlers['onTwoFactorCheck'];
    getCaptchaToken: () => string;
}

function useCredentialsSubmitHandler({ onTwoFactorCheck, getCaptchaToken }: CredentialsSubmitHandlerOptions) {
    const dispatch = useCommanderThunkDispatch();
    const { mutateAsync: signIn } = useRepositoryMutation('generalUsers', 'signIn');

    const onSubmit = async ({ password, nameOrEmail }: SignInFormValues) =>
        signIn({
            ...INITIAL_SIGN_IN_PARAMS,
            device: getDeviceFingerprintPayload(),
            password,
            identifier: nameOrEmail,
            method: GeneralSignInMethod.Credentials,
            application: GeneralArea.Commander,
            captchaVerificationToken: getCaptchaToken(),
        })
            .then(async (data) => dispatch(checkForConsent(data)))
            .then(async (data) => checkFor2FA({ ...data, nameOrEmail, onTwoFactorCheck }))
            .then(async (data) => dispatch(checkForHardDeviceApproval(data)))
            .then(async (data) => dispatch(checkForForced2FA(data)))
            .then(async (data) => dispatch(initializeUserSession(data)))
            // eslint-disable-next-line @mews/no-empty-catch
            .catch(() => {
                // Mute errors from promise rejections in dispatch calls.
                // Mutation errors are handled in the mutation itself.
            });

    return { onSubmit, resolver: signInCredentialsResolver };
}

async function checkFor2FA({
    authenticationInfo,
    nameOrEmail,
    onTwoFactorCheck,
    ...other
}: GeneralUserAccessResult & {
    nameOrEmail: string;
} & Pick<SignInEventHandlers, 'onTwoFactorCheck'>) {
    if (authenticationInfo && authenticationInfo.authenticator !== GeneralAuthenticatorType.None) {
        onTwoFactorCheck({ type: authenticationInfo.authenticator, email: nameOrEmail });
        return Promise.reject();
    }

    return Promise.resolve({ authenticationInfo, ...other });
}
